import {
  EURC_ADDRESS_ARC,
  MAIN_USD_STABLE_ADDRESSES,
  USDC_ADDRESSES,
  WRAPPED_NATIVE_ADDRESSES,
} from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import type { ChainAddress } from '@/utils/models';
import { areEquivalentTokenAddresses } from '@/utils/multichain';
import { resolveDefaultTokenPair } from '@/utils/swap';
import type { TokenInfo } from '@/utils/token.types';

/**
 * Opposite-side token that keeps the pair on the chain of a freshly selected
 * token, or `undefined` when the other side should stay. The caller checks
 * whether a market sell selection changed chains; advanced is single-chain.
 * Advanced orders reject native buys, so they pair USDC (or the main USD
 * stable), or the wrapped native when that stable is the selection - EURC on
 * Arc, which has no wrapped native.
 */
export function resolveSharedChainToken(
  selected: TokenInfo,
  side: 'buy' | 'sell',
  otherChainId: number | undefined,
  { advanced }: { advanced: boolean },
): ChainAddress | undefined {
  const { address, chainId } = selected;
  if (otherChainId === undefined || chainId === otherChainId) return undefined;
  if (side === 'buy' && !advanced) return undefined;

  if (advanced) {
    const stable = USDC_ADDRESSES[chainId] ?? MAIN_USD_STABLE_ADDRESSES[chainId];
    const stableAlternative =
      chainId === CHAIN_IDS.ARC ? EURC_ADDRESS_ARC : WRAPPED_NATIVE_ADDRESSES[chainId];
    const candidate = areEquivalentTokenAddresses(address, stable, chainId)
      ? stableAlternative
      : stable;
    if (candidate) return candidate;
  }

  return side === 'sell'
    ? resolveDefaultTokenPair({ sellTokenAddress: address, sellTokenChainId: chainId })
        .buyTokenAddress
    : resolveDefaultTokenPair({ buyTokenAddress: address, buyTokenChainId: chainId })
        .sellTokenAddress;
}
